<script setup>
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import { TrashIcon } from '@heroicons/vue/24/outline';
import { BookmarkIcon as BookmarkSolidIcon } from '@heroicons/vue/24/solid';
import { BookmarkIcon as BookmarkOutlineIcon } from '@heroicons/vue/24/outline';
import 'flag-icons/css/flag-icons.min.css';

const props = defineProps({
    savedRoutes: Array,
    activeSavedRouteId: Number,
    animateRoute: Boolean,
});

const emit = defineEmits(['loadRoute', 'deleteRoute', 'toggleBookmark']);

// The list comes pre-sorted from the server (bookmarked first, then most
// recent). Splitting into two collections lets us render each under its
// own header without re-sorting on the client.
const bookmarkedRoutes = computed(() => (props.savedRoutes ?? []).filter((r) => r.bookmarked));
const recentRoutes = computed(() => (props.savedRoutes ?? []).filter((r) => !r.bookmarked));

// Reactive "now" so "2m ago" labels update without a parent re-render. 60s
// granularity is enough since the smallest unit shown is minutes.
const now = ref(Date.now());
let nowInterval = null;

onMounted(() => {
    nowInterval = setInterval(() => {
        now.value = Date.now();
    }, 60_000);
});

onBeforeUnmount(() => {
    if (nowInterval) {
        clearInterval(nowInterval);
        nowInterval = null;
    }
});

const relativeTime = (iso) => {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    const diff = (now.value - then) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(iso).toLocaleDateString();
};
</script>

<template>
    <!-- Saved Routes — bookmarked entries pinned at the top under their
         own header so users can find their kept routes without scrolling
         past every recent calculation. -->
    <template v-if="bookmarkedRoutes.length > 0">
        <div class="border-b border-gray-200 px-4 pt-3 pb-2">
            <h3 class="text-xs font-semibold uppercase tracking-wide text-gray-500">Saved Routes</h3>
        </div>
        <ul class="divide-y divide-gray-100">
            <li
                v-for="savedRoute in bookmarkedRoutes"
                :key="savedRoute.id"
                class="group flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-indigo-50"
                :class="activeSavedRouteId === savedRoute.id ? 'bg-indigo-100' : ''"
                @click="!animateRoute && emit('loadRoute', savedRoute)">
                <span
                    role="button"
                    @click.stop="emit('toggleBookmark', savedRoute)"
                    class="shrink-0 cursor-pointer rounded p-1 text-amber-500 transition-colors hover:text-amber-600"
                    title="Remove bookmark">
                    <BookmarkSolidIcon class="h-4 w-4" />
                </span>
                <div class="min-w-0 flex-1 text-xs font-medium text-gray-900">
                    <div class="flex items-center gap-1">
                        <span v-if="savedRoute.ports[0]?.country_code" :class="`fi fi-${savedRoute.ports[0].country_code.toLowerCase()}`" class="!h-2.5 !w-3.5 rounded-xs"></span>
                        <span class="truncate">{{ savedRoute.ports[0]?.name || '?' }}</span>
                    </div>
                    <div class="flex items-center gap-1">
                        <span v-if="savedRoute.ports[savedRoute.ports.length - 1]?.country_code" :class="`fi fi-${savedRoute.ports[savedRoute.ports.length - 1].country_code.toLowerCase()}`" class="!h-2.5 !w-3.5 rounded-xs"></span>
                        <span class="truncate">{{ savedRoute.ports[savedRoute.ports.length - 1]?.name || '?' }}</span>
                        <span v-if="savedRoute.ports.length > 2" class="shrink-0 rounded bg-gray-100 px-1 py-px text-[9px] font-medium text-gray-600">
                            +{{ savedRoute.ports.length - 2 }}
                        </span>
                    </div>
                </div>
                <span
                    role="button"
                    @click.stop="emit('deleteRoute', savedRoute)"
                    class="shrink-0 cursor-pointer rounded p-1 text-gray-300 opacity-0 transition-opacity hover:text-red-500 group-hover:opacity-100"
                    title="Delete saved route">
                    <TrashIcon class="h-3.5 w-3.5" />
                </span>
            </li>
        </ul>
    </template>

    <!-- Recents — auto-tracked calculations. -->
    <div class="border-b border-gray-200 px-4 pt-3 pb-2" :class="bookmarkedRoutes.length > 0 ? 'border-t' : ''">
        <h3 class="text-xs font-semibold uppercase tracking-wide text-gray-500">Recents</h3>
    </div>
    <ul class="divide-y divide-gray-100">
        <li
            v-for="savedRoute in recentRoutes"
            :key="savedRoute.id"
            class="group flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-indigo-50"
            :class="activeSavedRouteId === savedRoute.id ? 'bg-indigo-100' : ''"
            @click="!animateRoute && emit('loadRoute', savedRoute)">
            <span
                role="button"
                @click.stop="emit('toggleBookmark', savedRoute)"
                class="shrink-0 cursor-pointer rounded p-1 text-gray-300 transition-colors hover:text-amber-500"
                title="Bookmark to keep">
                <BookmarkOutlineIcon class="h-4 w-4" />
            </span>
            <div class="min-w-0 flex-1 text-xs font-medium text-gray-900">
                <div class="flex items-center gap-1">
                    <span v-if="savedRoute.ports[0]?.country_code" :class="`fi fi-${savedRoute.ports[0].country_code.toLowerCase()}`" class="!h-2.5 !w-3.5 rounded-xs"></span>
                    <span class="truncate">{{ savedRoute.ports[0]?.name || '?' }}</span>
                </div>
                <div class="flex items-center gap-1">
                    <span v-if="savedRoute.ports[savedRoute.ports.length - 1]?.country_code" :class="`fi fi-${savedRoute.ports[savedRoute.ports.length - 1].country_code.toLowerCase()}`" class="!h-2.5 !w-3.5 rounded-xs"></span>
                    <span class="truncate">{{ savedRoute.ports[savedRoute.ports.length - 1]?.name || '?' }}</span>
                    <span v-if="savedRoute.ports.length > 2" class="shrink-0 rounded bg-gray-100 px-1 py-px text-[9px] font-medium text-gray-600">
                        +{{ savedRoute.ports.length - 2 }}
                    </span>
                </div>
                <div v-if="savedRoute.last_used_at" class="mt-0.5 text-[10px] font-normal text-gray-400">
                    {{ relativeTime(savedRoute.last_used_at) }}
                </div>
            </div>
            <span
                role="button"
                @click.stop="emit('deleteRoute', savedRoute)"
                class="shrink-0 cursor-pointer rounded p-1 text-gray-300 opacity-0 transition-opacity hover:text-red-500 group-hover:opacity-100"
                title="Remove from recents">
                <TrashIcon class="h-3.5 w-3.5" />
            </span>
        </li>
    </ul>
    <div v-if="recentRoutes.length === 0 && bookmarkedRoutes.length === 0" class="px-4 py-8 text-center text-xs text-gray-400">
        Recent calculations will appear here
    </div>
</template>
